<template>
  <div
    class="p-dropdown p-component p-inputwrapper custom-watchlist-dropdown"
    :class="{ 'p-disabled': disabled, 'p-focus': isOpen }"
    ref="dropdownContainer"
  >
    <!-- Dropdown Trigger / Selected Label -->
    <div class="dropdown-trigger-area" @click.stop="toggleDropdown">
      <span
        class="p-dropdown-label p-inputtext selected-text"
        :class="{ 'p-placeholder': !selectedLabel }"
        :title="selectedLabel"
      >
        {{ selectedLabel || placeholder }}
      </span>
      <div class="p-dropdown-trigger">
        <span
          class="p-dropdown-trigger-icon pi pi-chevron-down chevron-icon"
          :class="{ 'is-open': isOpen }"
        ></span>
      </div>
    </div>

    <!-- Floating Dropdown Overlay Panel (Opens downward) -->
    <transition name="dropdown-fade">
      <div
        v-if="isOpen"
        class="p-dropdown-panel p-component custom-dropdown-panel"
        ref="panelRef"
        @click.stop
        @mousedown.stop
      >
        <div class="dropdown-scroll-wrapper" ref="scrollWrapper">
          <transition-group
            name="watchlist-flip"
            tag="ul"
            class="p-dropdown-items custom-dropdown-list"
            :class="{ 'is-drag-active': isDragging }"
            ref="listRef"
          >
            <li
              v-for="(item, index) in localItems"
              :key="item.id || item.name || index"
              class="custom-watchlist-item"
              :class="{
                'is-selected': isItemSelected(item, index),
                'is-item-drag-ready': holdIndex === index && !isDragging,
                'is-item-dragging': dragCurrentIndex === index && isDragging,
                'is-item-over': dragTargetIndex === index && isDragging && dragCurrentIndex !== index
              }"
              @mousedown="onItemMouseDown(index, $event)"
              @click="onItemClick(index)"
            >
              <div class="item-inner">
                <span
                  class="item-drag-grip"
                  title="Drag to reorder"
                  @touchstart.stop="onGripTouchStart(index, $event)"
                  @mousedown.stop="onGripMouseDown(index, $event)"
                >
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor">
                    <circle cx="2" cy="2" r="1.2" />
                    <circle cx="6" cy="2" r="1.2" />
                    <circle cx="2" cy="7" r="1.2" />
                    <circle cx="6" cy="7" r="1.2" />
                    <circle cx="2" cy="12" r="1.2" />
                    <circle cx="6" cy="12" r="1.2" />
                  </svg>
                </span>
                <span class="item-name" :title="item.name">{{ item.name }}</span>
                <span v-if="isItemSelected(item, index)" class="item-check-icon">
                  <i class="pi pi-check"></i>
                </span>
              </div>
            </li>
          </transition-group>
        </div>

        <!-- Micro Helper Footer -->
        <div class="dropdown-footer-tip">
          <i class="pi pi-info-circle"></i>
          <span>Click to select &bull; Drag to reorder</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'DraggableWatchlistDropdown',
  props: {
    value: {
      type: [Number, String],
      default: -1
    },
    options: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: '--NONE--'
    }
  },
  data() {
    return {
      isOpen: false,
      localItems: [],
      selectedItemId: null,
      holdIndex: null,
      holdTimer: null,
      isDragging: false,
      dragStartIndex: null,
      dragCurrentIndex: null,
      dragTargetIndex: null,
      hasMoved: false,
      startY: 0,
      startX: 0,
      initialOrder: [],
      slotCenters: [],
      lastSwapTime: 0,
      wasJustDragging: false
    }
  },
  watch: {
    options: {
      immediate: true,
      handler(newVal) {
        if (!this.isDragging && Array.isArray(newVal)) {
          this.localItems = [...newVal]
          this.syncSelectedItem()
        }
      }
    },
    value: {
      immediate: true,
      handler() {
        if (!this.isDragging) {
          this.syncSelectedItem()
        }
      }
    }
  },
  computed: {
    selectedLabel() {
      if (this.selectedItemId && this.localItems) {
        const item = this.localItems.find(
          (i) => (i.id || i.name) === this.selectedItemId
        )
        if (item) return item.name
      }
      const idx = Number(this.value)
      if (!isNaN(idx) && idx >= 0 && this.localItems && this.localItems[idx]) {
        return this.localItems[idx].name
      }
      return ''
    }
  },
  methods: {
    syncSelectedItem() {
      const idx = Number(this.value)
      if (!isNaN(idx) && idx >= 0 && this.localItems && this.localItems[idx]) {
        this.selectedItemId = this.localItems[idx].id || this.localItems[idx].name
      } else {
        this.selectedItemId = null
      }
    },
    isItemSelected(item, index) {
      if (this.selectedItemId && (item.id || item.name)) {
        return (item.id || item.name) === this.selectedItemId
      }
      return Number(this.value) === index
    },
    toggleDropdown() {
      if (this.disabled) return
      if (this.isOpen) {
        this.closeDropdown()
      } else {
        this.openDropdown()
      }
    },
    openDropdown() {
      this.localItems = [...this.options]
      this.syncSelectedItem()
      this.wasJustDragging = false
      this.isOpen = true
      setTimeout(() => {
        document.addEventListener('click', this.onDocumentClick)
      }, 0)
    },
    closeDropdown() {
      if (this.isDragging) return
      this.isOpen = false
      document.removeEventListener('click', this.onDocumentClick)
      this.clearHoldState()
    },
    onDocumentClick(e) {
      if (this.$el && !this.$el.contains(e.target)) {
        this.closeDropdown()
      }
    },
    onItemClick(index) {
      if (this.wasJustDragging || this.isDragging || !this.isOpen) return
      this.selectItem(index)
    },
    selectItem(index) {
      if (index !== null && index >= 0 && this.localItems && this.localItems[index]) {
        const clickedItem = this.localItems[index]
        if (clickedItem) {
          this.selectedItemId = clickedItem.id || clickedItem.name
        }
        this.$emit('input', index)
      }
      this.closeDropdown()
    },
    // Desktop mouse down on item row: 250ms hold-to-drag or quick click to select
    onItemMouseDown(index, event) {
      if (event.button !== 0) return
      this.startHold(index, event.clientX, event.clientY, false)
      window.addEventListener('mousemove', this.onMouseMove)
      window.addEventListener('mouseup', this.onMouseUp)
    },
    // Desktop mouse down on grip handle: immediate drag initiation
    onGripMouseDown(index, event) {
      if (event.button !== 0) return
      this.startHold(index, event.clientX, event.clientY, true)
      window.addEventListener('mousemove', this.onMouseMove)
      window.addEventListener('mouseup', this.onMouseUp)
    },
    // Mobile touch on grip handle: dedicated reorder without blocking row scrolling
    onGripTouchStart(index, event) {
      if (event.touches.length !== 1) return
      const touch = event.touches[0]
      this.startHold(index, touch.clientX, touch.clientY, true)
      window.addEventListener('touchmove', this.onTouchMove, { passive: false })
      window.addEventListener('touchend', this.onTouchEnd)
      window.addEventListener('touchcancel', this.onTouchEnd)
    },
    startHold(index, clientX, clientY, isImmediate = false) {
      this.clearHoldTimer()
      this.holdIndex = index
      this.dragStartIndex = index
      this.dragCurrentIndex = index
      this.dragTargetIndex = index
      this.startX = clientX
      this.startY = clientY
      this.hasMoved = false
      this.initialOrder = [...this.localItems]
      this.lastSwapTime = 0

      if (isImmediate) {
        this.isDragging = true
        this.initSlotCenters()
        document.body.style.cursor = 'grabbing'
        document.body.style.userSelect = 'none'
      } else {
        // Desktop hold threshold: 250ms to turn draggable
        this.holdTimer = setTimeout(() => {
          this.isDragging = true
          this.initSlotCenters()
          document.body.style.cursor = 'grabbing'
          document.body.style.userSelect = 'none'
        }, 250)
      }
    },
    initSlotCenters() {
      const listEl = this.$refs.listRef?.$el || this.$refs.listRef
      if (!listEl) return
      const itemEls = Array.from(listEl.querySelectorAll('.custom-watchlist-item'))
      this.slotCenters = itemEls.map((el) => el.offsetTop + el.offsetHeight / 2)
    },
    onMouseMove(event) {
      this.handlePointerMove(event.clientX, event.clientY)
    },
    onTouchMove(event) {
      if (this.isDragging) {
        event.preventDefault()
      }
      if (event.touches.length > 0) {
        this.handlePointerMove(event.touches[0].clientX, event.touches[0].clientY)
      }
    },
    handlePointerMove(clientX, clientY) {
      const distY = Math.abs(clientY - this.startY)
      const distX = Math.abs(clientX - this.startX)

      if (distY > 4 || distX > 4) {
        this.hasMoved = true
      }

      if (!this.isDragging) return
      if (!this.slotCenters || this.slotCenters.length < 2) return

      const wrapper = this.$refs.scrollWrapper
      const listEl = this.$refs.listRef?.$el || this.$refs.listRef
      if (!wrapper || !listEl) return

      const wrapperRect = wrapper.getBoundingClientRect()
      const listRect = listEl.getBoundingClientRect()

      // Auto-scroll when near top or bottom edges
      const edgeThreshold = 28
      const scrollSpeed = 5
      if (clientY < wrapperRect.top + edgeThreshold) {
        wrapper.scrollTop -= scrollSpeed
      } else if (clientY > wrapperRect.bottom - edgeThreshold) {
        wrapper.scrollTop += scrollSpeed
      }

      // Vertical position relative to the top of the list container
      const yInList = clientY - listRect.top
      let targetIdx = this.dragCurrentIndex

      // Midpoint hysteresis:
      // Moving down requires passing past the midpoint of the lower slot
      while (
        targetIdx < this.slotCenters.length - 1 &&
        yInList > this.slotCenters[targetIdx + 1]
      ) {
        targetIdx++
      }

      // Moving up requires passing above the midpoint of the upper slot
      while (
        targetIdx > 0 &&
        yInList < this.slotCenters[targetIdx - 1]
      ) {
        targetIdx--
      }

      if (targetIdx !== this.dragCurrentIndex) {
        // Enforce a small cooldown (60ms) between swaps to ensure silky-smooth animations
        const now = Date.now()
        if (now - this.lastSwapTime < 60) return
        this.lastSwapTime = now

        const itemToMove = this.localItems.splice(this.dragCurrentIndex, 1)[0]
        this.localItems.splice(targetIdx, 0, itemToMove)
        this.dragCurrentIndex = targetIdx
        this.dragTargetIndex = targetIdx
      }
    },
    onMouseUp(event) {
      window.removeEventListener('mousemove', this.onMouseMove)
      window.removeEventListener('mouseup', this.onMouseUp)
      this.handlePointerEnd()
    },
    onTouchEnd(event) {
      window.removeEventListener('touchmove', this.onTouchMove)
      window.removeEventListener('touchend', this.onTouchEnd)
      window.removeEventListener('touchcancel', this.onTouchEnd)
      this.handlePointerEnd()
    },
    handlePointerEnd() {
      const wasDragging = this.isDragging
      this.clearHoldTimer()

      document.body.style.cursor = ''
      document.body.style.userSelect = ''

      if (wasDragging) {
        this.wasJustDragging = true
        setTimeout(() => {
          this.wasJustDragging = false
        }, 150)

        // Reordered via drag & drop
        const orderChanged = this.localItems.some(
          (item, idx) => (item.id || item.name) !== (this.initialOrder[idx]?.id || this.initialOrder[idx]?.name)
        )
        if (orderChanged) {
          this.$emit('reorder', [...this.localItems])
        }
      } else {
        // Quick click without dragging -> Select the item
        if (!this.hasMoved && this.holdIndex !== null && this.holdIndex >= 0) {
          this.selectItem(this.holdIndex)
        }
      }

      this.clearHoldState()
    },
    clearHoldTimer() {
      if (this.holdTimer) {
        clearTimeout(this.holdTimer)
        this.holdTimer = null
      }
    },
    clearHoldState() {
      this.clearHoldTimer()
      this.holdIndex = null
      this.isDragging = false
      this.dragStartIndex = null
      this.dragCurrentIndex = null
      this.dragTargetIndex = null
      this.hasMoved = false
      this.slotCenters = []
      this.lastSwapTime = 0
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  },
  beforeDestroy() {
    this.clearHoldState()
    document.removeEventListener('click', this.onDocumentClick)
    window.removeEventListener('mousemove', this.onMouseMove)
    window.removeEventListener('mouseup', this.onMouseUp)
    window.removeEventListener('touchmove', this.onTouchMove)
    window.removeEventListener('touchend', this.onTouchEnd)
    window.removeEventListener('touchcancel', this.onTouchEnd)
  }
}
</script>

<style scoped>
.custom-watchlist-dropdown {
  position: relative;
  user-select: none;
  display: inline-flex;
  cursor: pointer;
  border: 1px solid var(--border-color, #ced4da);
  background: var(--tool-background, #ffffff);
  border-radius: 4px;
  height: 35px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.custom-watchlist-dropdown.p-focus {
  border-color: #2196f3;
  box-shadow: 0 0 0 0.15rem rgba(33, 150, 243, 0.2);
}

.dropdown-trigger-area {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

.selected-text {
  flex: 1;
  padding: 0 0.75rem;
  font-size: 0.875rem;
  color: var(--text-color, #333333);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: transparent !important;
  border: none !important;
}

.selected-text.p-placeholder {
  color: #94a3b8;
}

.p-dropdown-trigger {
  width: 2.2rem;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  flex-shrink: 0;
}

.chevron-icon {
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

.chevron-icon.is-open {
  transform: rotate(180deg);
}

/* Floating Overlay Panel */
.custom-dropdown-panel {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  width: 100%;
  min-width: 180px;
  background: var(--tool-background, #ffffff);
  border: 1px solid var(--border-color, #ced4da);
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
  z-index: 9999 !important;
  overflow: hidden;
}

.dropdown-scroll-wrapper {
  max-height: 220px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 0.35rem 0;
}

/* Custom sleek scrollbar */
.dropdown-scroll-wrapper::-webkit-scrollbar {
  width: 5px;
}
.dropdown-scroll-wrapper::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 3px;
}

.custom-dropdown-list {
  position: relative;
  padding: 0;
  margin: 0;
  list-style: none;
}

/* While drag is active, disable pointer events on items so hover states do not flash */
.custom-dropdown-list.is-drag-active {
  user-select: none;
}

.custom-dropdown-list.is-drag-active .custom-watchlist-item {
  pointer-events: none;
}

/* Normal hover only when NOT dragging */
.custom-dropdown-list:not(.is-drag-active) .custom-watchlist-item:hover {
  background-color: rgba(33, 150, 243, 0.08);
}

.custom-dropdown-list:not(.is-drag-active) .custom-watchlist-item:hover .item-drag-grip {
  color: #2196f3;
}

/* Item styling */
.custom-watchlist-item {
  position: relative;
  margin: 2px 4px;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
  color: var(--text-color, #333333);
  box-sizing: border-box;
  touch-action: pan-y;
  -webkit-tap-highlight-color: transparent;
}

.custom-watchlist-item.is-selected {
  background-color: #e3f2fd !important;
  color: #1976d2 !important;
  font-weight: 600;
}

.item-inner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.item-drag-grip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  cursor: grab;
  padding: 6px 8px;
  margin: -4px 2px -4px -6px;
  border-radius: 4px;
  transition: color 0.15s ease, background-color 0.15s ease;
  flex-shrink: 0;
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
}

.item-drag-grip:hover,
.item-drag-grip:active {
  color: #2196f3;
  background-color: rgba(33, 150, 243, 0.1);
}

.item-name {
  flex: 1;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-check-icon {
  font-size: 0.8rem;
  color: #1976d2;
  margin-left: auto;
  flex-shrink: 0;
}

/* Dragging active state */
.custom-watchlist-item.is-item-dragging {
  background-color: #e3f2fd !important;
  color: #1976d2 !important;
  box-shadow: 0 4px 14px rgba(33, 150, 243, 0.28);
  border: 1px dashed #2196f3;
  z-index: 10;
  opacity: 0.95;
  cursor: grabbing !important;
}

.custom-watchlist-item.is-item-dragging .item-drag-grip {
  cursor: grabbing !important;
  color: #1976d2;
}

/* Reorder smooth slide transition */
.watchlist-flip-move {
  transition: transform 0.22s cubic-bezier(0.2, 0, 0, 1);
}

/* Micro Helper Footer */
.dropdown-footer-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  font-size: 0.72rem;
  color: #94a3b8;
  padding: 0.4rem 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.02);
  user-select: none;
}

.dropdown-footer-tip i {
  font-size: 0.7rem;
}

/* Dropdown open/close fade transition */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-fade-enter,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>

